import type { CSSProperties, ReactNode } from "react";

import { cn } from "@btnet/utilities-core";

import "./BuiText.less";

export type BuiFontSize =
  | "normal-sm"
  | "normal-md"
  | "normal-lg"
  | "distinct-sm"
  | "distinct-md"
  | "distinct-lg"
  | "heavy-sm"
  | "heavy-md"
  | "heavy-lg"
  | "heavy-xl";

type BuiTextTagType =
  | "div"
  | "pre"
  | "span"
  | "p"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6";

/**
 * Pass children to BuiText to render content.
 * @example
 * ```tsx
 * <BuiText size="normal-md">
 *  My text can be <b>bold.</b>
 * </BuiText>
 * ```
 */
interface BuiChildTextContent {
  text?: never;
  children: ReactNode;
}

/**
 * Optional method for passing text value. Passing Children is the preferred approach.
 */
interface BuiTextPropContent {
  text: string | ReactNode;
  children?: never;
}

type BuiTextContent = BuiChildTextContent | BuiTextPropContent;

export type BuiTextProps = {
  /**
   * Id applied to the rendered tag.
   */
  id?: string;

  /**
   * data-testid applied to the rendered tag for testing purposes.
   */
  testid?: string;

  /**
   * Specifies which tag to render; will override the default tag type chosen by specifying size.
   * @example
   * ```tsx
   * <BuiText size="distinct-sm" tag="span">Example inline text</BuiText>
   */
  tag?: BuiTextTagType;

  /**
   * Controls font size and default tag.
   * Size arguments starting with "distinct" or "heavy" render as header tags with display: block.
   * Size arguments starting with "normal" render as spans by default.
   */
  size?: BuiFontSize;

  /**
   * Class name to pass to the rendered tag.
   */
  className?: string;
  style?: CSSProperties;
} & BuiTextContent;

export function getTextTag(size: BuiFontSize): BuiTextTagType {
  switch (size) {
    case "distinct-sm":
    case "distinct-md":
    case "distinct-lg":
      return "h5";
    case "heavy-sm":
      return "h4";
    case "heavy-md":
      return "h3";
    case "heavy-lg":
      return "h2";
    case "heavy-xl":
      return "h1";
    default:
      return "span";
  }
}

/**
 * BuiText is the Blueprint typography primitive for rendering text content.
 * The `size` prop drives both the font styling and the default HTML element:
 * - `normal-*` sizes render as inline `<span>` elements
 * - `distinct-*` and `heavy-*` sizes render as block-display header tags
 *   (`<h1>`–`<h5>`)
 *
 * Use `tag` to override the element chosen by `size` (e.g. an inline `heavy-*`
 * style without the header semantics). Provide content via `children` (preferred)
 * or the `text` prop.
 * @param props - The text component properties
 * @param props.id - Id applied to the rendered tag
 * @param props.testid - Test ID applied to the rendered tag for testing purposes
 * @param props.tag - Overrides the default tag chosen by `size`
 * @param props.text - Text content (use `children` instead when possible)
 * @param props.size - Controls font size and the default tag (defaults to `normal-sm`)
 * @param props.className - Additional CSS classes to apply
 * @param props.style - Inline styles to apply
 * @param props.children - Text content (preferred over `text`)
 * @example
 * ```tsx
 * <BuiText size="heavy-md">Section heading</BuiText>
 *
 * <BuiText size="normal-md">
 *   My text can be <b>bold.</b>
 * </BuiText>
 *
 * <BuiText size="distinct-sm" tag="span">Inline distinct text</BuiText>
 * ```
 */
export function BuiText({
  id,
  testid,
  tag,
  text,
  size = "normal-sm",
  className,
  style,
  children,
}: BuiTextProps) {
  const Tag: BuiTextTagType = tag ?? getTextTag(size);
  return (
    <Tag
      id={id}
      data-testid={testid}
      className={cn(`bui-text`, `bui-text--${size}`, className)}
      style={style}
    >
      {text ?? children ?? ""}
    </Tag>
  );
}
